// Расчёты экрана «Unit»: сервер отдаёт показатели сотрудников помесячно,
// здесь они сворачиваются в выбранный период (квартал или год).

export type UnitRole = "production" | "gip" | "nonproduction";
export type UnitOrderBasis = "actual" | "capped" | "contract" | "portfolio" | "none";
export type ReasonType = "overtime" | "premium" | "indexation" | "absence" | "other";

/** Код периода: 1..4 — квартал, 0 — весь год */
export type PeriodCode = 0 | 1 | 2 | 3 | 4;
export const YEAR_PERIOD: PeriodCode = 0;

export interface UnitMonth {
    month: number;
    production: boolean;
    role: UnitRole;
    department: string;
    position: string;
    normHours: number;
    availableHours: number;
    workedHours: number;
    projectHours: number;
    overtimeHours: number;
    vacationHours: number;
    illnessHours: number;
    planCost: number;
    factCost: number;
    projectLabor: number;
    output: number;
    overhead: number;
}

export interface UnitProjectPart {
    orderId: string;
    month: number;
    hours: number;
    labor: number;
    output: number;
}

export interface UnitEmployee {
    id: string;
    name: string;
    /** Фамилия с инициалами — с сервера (Employer.ShortName) */
    shortName: string;
    department: string;
    position: string;
    months: UnitMonth[];
    projects: UnitProjectPart[];
}

export interface UnitOrder {
    id: string;
    name: string;
    number: string;
    color: string;
    status: string;
    price: number;
    revenue: number;
    ownLabor: number;
    otherCosts: number;
    ratio: number;
    basis: UnitOrderBasis;
}

export interface UnitQuarterRate {
    quarter: number;
    variableRate: number;
    overheadPercents: number;
    income: number;
    ownSpendings: number;
}

export interface UnitBenchmarks {
    gip: number;
    production: number;
    nonProduction: number;
}

export interface UnitReason {
    id: string;
    employerId: string;
    year: number;
    quarter: number;
    reasons: ReasonType[];
    comment: string;
    author: { id: string; name: string };
    modified: string;
}

export interface UnitEconomicsData {
    year: number;
    years: number[];
    lastMonth: number;
    deviationThreshold: number;
    portfolioRatio: number;
    ratioCap: number;
    employees: UnitEmployee[];
    orders: UnitOrder[];
    rates: UnitQuarterRate[];
    benchmarks: UnitBenchmarks;
    reasons: UnitReason[];
}

/** Строка сотрудника за период */
export interface EmployeeRow {
    id: string;
    name: string;
    shortName: string;
    department: string;
    position: string;
    role: UnitRole;
    /** Хотя бы один месяц периода — на производственной должности */
    production: boolean;
    normHours: number;
    availableHours: number;
    workedHours: number;
    projectHours: number;
    overtimeHours: number;
    vacationHours: number;
    illnessHours: number;
    planCost: number;
    factCost: number;
    output: number;
    overhead: number;
    margin: number;
    /** Плановая стоимость часа */
    planHour: number;
    /**
     * Фактическая стоимость оплаченного часа: затраты ÷ (отработано + отпуск + больничный).
     * План по штатному размазывает резерв отпусков по месяцам, поэтому с ним сравниваются
     * оплаченные часы, а не только отработанные — иначе каждый отпуск выглядел бы удорожанием
     */
    factHour: number;
    /** Стоимость отработанного часа: затраты ÷ отработанные часы (справочно) */
    workedHour: number;
    /** Отклонение факта от плана, %; null — сравнивать не с чем */
    deviation: number | null;
    anomaly: boolean;
    /** Загрузка проектами, %; null — нет доступных часов */
    utilization: number | null;
    benchmark: number;
    /** Маржа по кварталам года (null — сотрудник в квартале не работал) */
    trend: (number | null)[];
    reason: UnitReason | null;
    hints: ReasonType[];
}

export const roleLabels: Record<UnitRole, string> = {
    production: "Производственные",
    gip: "ГИП",
    nonproduction: "Непроизводственные",
};

/** Подпись роли у одного сотрудника (тег в строке и в карточке) */
export const roleTagLabels: Record<UnitRole, string> = {
    production: "Производственный",
    gip: "ГИП",
    nonproduction: "Непроизводственный",
};

/** Короткая подпись роли — тег во второй строке у сотрудника в таблице */
export const roleShortLabels: Record<UnitRole, string> = {
    production: "Произв.",
    gip: "ГИП",
    nonproduction: "Непроизв.",
};

export const reasonLabels: Record<ReasonType, string> = {
    overtime: "Переработка",
    premium: "Премия",
    indexation: "Индексация оклада",
    absence: "Отпуск / больничный",
    other: "Другое",
};

export const basisLabels: Record<UnitOrderBasis, string> = {
    actual: "закрыт: поступления ÷ затраты",
    capped: "закрыт, но отдача выше, чем у 90% закрытых проектов (затраты учтены не полностью) — взят 90-й перцентиль",
    contract: "в работе: сумма договора ÷ затраты на сегодня",
    portfolio: "в работе: сумма договора на рубль затрат выше средней — взята средняя отдача закрытых проектов",
    none: "договор не заключён — выручки нет",
};

export const quarterNames = ["I", "II", "III", "IV"];

export function periodLabel(period: PeriodCode, year: number) {
    return period === YEAR_PERIOD ? `${year} год` : `${quarterNames[period - 1]} квартал ${year}`;
}

export function periodMonths(period: PeriodCode): number[] {
    if (period === YEAR_PERIOD) {
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    }

    const first = (period - 1) * 3 + 1;
    return [first, first + 1, first + 2];
}

/** Незакрытый квартал текущего года — данные по нему неполные; 0, если такого нет */
export function quarterInProgress(data: UnitEconomicsData | null, now = new Date()) {
    if (!data || data.year !== now.getFullYear()) {
        return 0;
    }

    return data.lastMonth % 3 === 0 && now.getMonth() + 1 > data.lastMonth ? 0 : Math.ceil(data.lastMonth / 3);
}

/** Кварталы периода, за которые ещё нет ставки накладных */
export function quartersWithoutRate(data: UnitEconomicsData | null, period: PeriodCode) {
    if (!data) {
        return [];
    }

    const last = Math.ceil(data.lastMonth / 3);
    const quarters = period === YEAR_PERIOD ? [1, 2, 3, 4].filter((q) => q <= last) : [period];
    return quarters.filter((q) => !(data.rates.find((r) => r.quarter === q)?.variableRate ?? 0));
}

export function quarterOf(month: number) {
    return Math.floor((month - 1) / 3) + 1;
}

const sum = <T>(items: T[], get: (item: T) => number) => items.reduce((acc, item) => acc + get(item), 0);

export function benchmarkFor(role: UnitRole, benchmarks: UnitBenchmarks) {
    return role === "nonproduction" ? benchmarks.nonProduction : benchmarks[role];
}

/**
 * Вероятные причины отклонения стоимости часа — подсказка, что выбрать.
 * Смотрим на помесячные затраты: разовый всплеск факта над планом — премия, устойчивое
 * превышение во всех месяцах или рост оклада по штатному — индексация; часы — по табелю.
 */
export function suggestReasons(months: UnitMonth[], deviation: number | null): ReasonType[] {
    if (deviation === null) {
        return [];
    }

    const hints: ReasonType[] = [];
    const worked = sum(months, (m) => m.workedHours);
    const overtime = sum(months, (m) => m.overtimeHours);
    const absence = sum(months, (m) => m.vacationHours + m.illnessHours);
    const norm = sum(months, (m) => m.normHours);

    if (worked > 0 && overtime / worked >= 0.05) {
        hints.push("overtime");
    }

    if (norm > 0 && absence / norm >= 0.1) {
        hints.push("absence");
    }

    if (deviation > 0) {
        const planned = months.filter((m) => m.planCost > 0);
        const over = planned.map((m) => m.factCost / m.planCost - 1);
        const spikes = over.filter((o) => o >= 0.15).length;
        const steady = planned.length > 1 && over.every((o) => o >= 0.03);
        const raised = planned.length > 1 && planned[planned.length - 1].planCost > planned[0].planCost * 1.03;

        if (steady || raised) {
            hints.push("indexation");
        } else if (spikes > 0) {
            hints.push("premium");
        }
    }

    return hints;
}

function marginOf(months: UnitMonth[]) {
    return sum(months, (m) => (m.production ? m.output - m.factCost - m.overhead : 0));
}

export function buildRow(employee: UnitEmployee, period: PeriodCode, data: UnitEconomicsData): EmployeeRow | null {
    const range = periodMonths(period);
    const months = employee.months.filter((m) => range.includes(m.month));
    if (!months.length) {
        return null;
    }

    const last = months[months.length - 1];
    const production = months.some((m) => m.production);

    const normHours = sum(months, (m) => m.normHours);
    const workedHours = sum(months, (m) => m.workedHours);
    const availableHours = sum(months, (m) => m.availableHours);
    const projectHours = sum(months, (m) => m.projectHours);
    const planCost = sum(months, (m) => m.planCost);
    const factCost = sum(months, (m) => m.factCost);
    const output = sum(months, (m) => m.output);
    const overhead = sum(months, (m) => m.overhead);

    const vacationHours = sum(months, (m) => m.vacationHours);
    const illnessHours = sum(months, (m) => m.illnessHours);
    const paidHours = workedHours + vacationHours + illnessHours;

    const planHour = normHours > 0 ? planCost / normHours : 0;
    const factHour = paidHours > 0 ? factCost / paidHours : 0;
    const deviation = planHour > 0 && factHour > 0 ? ((factHour - planHour) / planHour) * 100 : null;
    const anomaly = deviation !== null && Math.abs(deviation) >= data.deviationThreshold;

    const trend = [1, 2, 3, 4].map((quarter) => {
        const quarterMonths = employee.months.filter((m) => quarterOf(m.month) === quarter);
        return quarterMonths.some((m) => m.production) ? marginOf(quarterMonths) : null;
    });

    const reason =
        data.reasons.find((r) => r.employerId === employee.id && r.year === data.year && r.quarter === period) ?? null;

    return {
        id: employee.id,
        name: employee.name,
        shortName: employee.shortName || employee.name,
        department: last.department || employee.department,
        position: last.position || employee.position,
        role: last.role,
        production,
        normHours,
        availableHours,
        workedHours,
        projectHours,
        overtimeHours: sum(months, (m) => m.overtimeHours),
        vacationHours,
        illnessHours,
        planCost,
        factCost,
        output,
        overhead,
        margin: marginOf(months),
        planHour,
        factHour,
        workedHour: workedHours > 0 ? factCost / workedHours : 0,
        deviation,
        anomaly,
        utilization: availableHours > 0 ? (projectHours / availableHours) * 100 : null,
        benchmark: benchmarkFor(last.role, data.benchmarks),
        trend,
        reason,
        hints: anomaly ? suggestReasons(months, deviation) : [],
    };
}

export function buildRows(data: UnitEconomicsData | null, period: PeriodCode): EmployeeRow[] {
    if (!data) {
        return [];
    }

    return data.employees.map((e) => buildRow(e, period, data)).filter((row): row is EmployeeRow => row !== null);
}

export interface RoleStats {
    role: UnitRole;
    count: number;
    /** Средневзвешенная загрузка роли, %; null — нет доступных часов */
    utilization: number | null;
    benchmark: number;
    belowCount: number;
    /** Сотрудники роли отмечают в табеле часы по проектам — загрузку можно измерить */
    tracked: boolean;
}

export function roleStats(rows: EmployeeRow[], benchmarks: UnitBenchmarks): RoleStats[] {
    return (["production", "gip", "nonproduction"] as UnitRole[]).map((role) => {
        const items = rows.filter((r) => r.role === role);
        const available = sum(items, (r) => r.availableHours);
        const benchmark = benchmarkFor(role, benchmarks);

        return {
            role,
            count: items.length,
            utilization: available > 0 ? (sum(items, (r) => r.projectHours) / available) * 100 : null,
            benchmark,
            belowCount: items.filter((r) => r.utilization !== null && r.utilization < benchmark).length,
            tracked: items.some((r) => r.projectHours > 0),
        };
    });
}

export interface Totals {
    count: number;
    factCost: number;
    output: number;
    overhead: number;
    margin: number;
    projectHours: number;
    availableHours: number;
    anomalies: number;
}

export function totals(rows: EmployeeRow[]): Totals {
    const production = rows.filter((r) => r.production);
    return {
        count: production.length,
        factCost: sum(production, (r) => r.factCost),
        output: sum(production, (r) => r.output),
        overhead: sum(production, (r) => r.overhead),
        margin: sum(production, (r) => r.margin),
        projectHours: sum(production, (r) => r.projectHours),
        availableHours: sum(production, (r) => r.availableHours),
        anomalies: rows.filter((r) => r.anomaly).length,
    };
}

/** Средняя ставка накладных за период, взвешенная по себестоимости труда */
export function periodRate(data: UnitEconomicsData | null, period: PeriodCode, rows: EmployeeRow[]) {
    if (!data) {
        return 0;
    }

    if (period !== YEAR_PERIOD) {
        return data.rates.find((r) => r.quarter === period)?.variableRate ?? 0;
    }

    const labor = sum(rows, (r) => (r.production ? r.factCost : 0));
    return labor > 0 ? sum(rows, (r) => (r.production ? r.overhead : 0)) / labor : 0;
}

export interface ProjectContribution {
    order: UnitOrder | null;
    orderId: string;
    hours: number;
    labor: number;
    output: number;
}

export function projectContributions(
    employee: UnitEmployee,
    period: PeriodCode,
    data: UnitEconomicsData
): ProjectContribution[] {
    const range = periodMonths(period);
    const map = new Map<string, ProjectContribution>();

    employee.projects
        .filter((p) => range.includes(p.month))
        .forEach((p) => {
            const current = map.get(p.orderId) ?? {
                order: data.orders.find((o) => o.id === p.orderId) ?? null,
                orderId: p.orderId,
                hours: 0,
                labor: 0,
                output: 0,
            };

            current.hours += p.hours;
            current.labor += p.labor;
            current.output += p.output;
            map.set(p.orderId, current);
        });

    return Array.from(map.values()).sort((a, b) => b.output - a.output);
}

export interface QuarterMarginRow {
    id: string;
    name: string;
    shortName: string;
    department: string;
    quarters: (number | null)[];
    total: number;
}

/** Маржа производственных сотрудников по кварталам года */
export function quarterMargins(data: UnitEconomicsData | null): QuarterMarginRow[] {
    if (!data) {
        return [];
    }

    return data.employees
        .filter((e) => e.months.some((m) => m.production))
        .map((e) => {
            const quarters = [1, 2, 3, 4].map((quarter) => {
                const months = e.months.filter((m) => quarterOf(m.month) === quarter);
                return months.some((m) => m.production) ? marginOf(months) : null;
            });

            const last = e.months[e.months.length - 1];

            return {
                id: e.id,
                name: e.name,
                shortName: e.shortName || e.name,
                department: last?.department || e.department,
                quarters,
                total: quarters.reduce<number>((acc, q) => acc + (q ?? 0), 0),
            };
        });
}
